/**
 * Server Notification Component
 * Popup notification for servers when their dine-in orders are ready
 */
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, Check, Table2 } from 'lucide-react';

export default function ServerNotification({ 
  notification, 
  onAcknowledge, 
  show = true 
}) {
  if (!notification || !show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -100, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -100, scale: 0.9 }}
        className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-md px-4"
      >
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border-2 border-green-500 overflow-hidden">
          {/* Header with bell animation */}
          <div className="bg-green-500 px-4 py-3 flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center animate-bounce">
              <Bell className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-white font-bold text-lg">Order Ready!</h3>
              <p className="text-green-100 text-sm">Pick up from counter/kitchen</p>
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            {/* Table & Order Number */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Table2 className="w-5 h-5 text-gray-400" />
                <span className="text-xl font-bold text-gray-900 dark:text-white">
                  Table {notification.table_number}
                </span>
              </div>
              <span className="font-mono text-sm text-gray-500 dark:text-gray-400">
                #{notification.order_number}
              </span>
            </div>

            {/* Items List */}
            {notification.items && notification.items.length > 0 && (
              <div className="space-y-2 mb-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">Items ready:</p>
                <div className="flex flex-wrap gap-2">
                  {notification.items.map((item, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-sm text-gray-700 dark:text-gray-300"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Acknowledge Button */}
            <button
              onClick={() => onAcknowledge?.(notification.id)}
              className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              <Check className="w-5 h-5" />
              Got it!
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
